import type { KeyboardEvent, ReactNode } from 'react'
import type { Seat } from '@/lib/seats'

import { useId, useState } from 'react'
import { cn } from '@/lib/utils'
import { SEATS } from '@/lib/seats'

interface SeatMapProps {
    taken: Set<string>
    selected: string[]
    onToggle: (id: string) => void
    className?: string
}

const ringLabels = ['A', 'B', 'C', 'D', 'E', 'F']
const ringRadii: Record<string, number> = { A: 0.32, B: 0.44, C: 0.56, D: 0.68, E: 0.8, F: 0.92 }

const position = (angle: number, radius: number) => ({ x: radius * Math.sin(angle), y: -radius * Math.cos(angle) })

const arcPath = (radius: number, from: number, to: number) => {
    const a = position(from, radius)
    const b = position(to, radius)
    const large = ((to - from + Math.PI * 2) % (Math.PI * 2)) > Math.PI ? 1 : 0

    return `M ${a.x} ${a.y} A ${radius} ${radius} 0 ${large} 1 ${b.x} ${b.y}`
}

const ARROWS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']

const angularGap = (a: number, b: number) => {
    const delta = Math.abs(a - b) % (Math.PI * 2)

    return delta > Math.PI ? Math.PI * 2 - delta : delta
}

const seatsInRing = (ring: string) => SEATS.filter(seat => seat.ring === ring)

/** Left and right step along a ring; up moves one ring in towards the projector, down one ring out. */
const neighbour = (seat: Seat, key: string): Seat | undefined => {
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
        const row = seatsInRing(seat.ring)
        const step = key === 'ArrowRight' ? 1 : -1

        return row[(seat.number - 1 + step + row.length) % row.length]
    }

    const ring = ringLabels[ringLabels.indexOf(seat.ring) + (key === 'ArrowDown' ? 1 : -1)]

    if (!ring) return undefined

    return seatsInRing(ring).reduce((best, candidate) => (angularGap(candidate.angle, seat.angle) < angularGap(best.angle, seat.angle) ? candidate : best))
}

const describe = (seat: Seat, state: string) => {
    const base = `Ring ${seat.ring}, seat ${seat.number}`

    if (seat.kind === 'held') return `${base}, presenter's seat, not for sale`
    if (seat.kind === 'wheelchair') return `${base}, wheelchair space, ${state}`

    return `${base}${seat.best ? ', best view' : ''}, ${state}`
}

/** A top-down plan of the dome: six rings of reclined seats around the star projector. */
export const SeatMap = ({ taken, selected, onToggle, className }: SeatMapProps): ReactNode => {
    const [focus, setFocus] = useState<Seat | null>(null)
    const hintId = useId()
    const selectedSet = new Set(selected)

    const handleKey = (event: KeyboardEvent<SVGGElement>, seat: Seat, unavailable: boolean) => {
        if (ARROWS.includes(event.key)) {
            const next = neighbour(seat, event.key)

            if (!next) return

            event.preventDefault()
            event.currentTarget.ownerSVGElement?.querySelector<SVGGElement>(`[data-seat='${next.id}']`)?.focus()
            return
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            if (!unavailable) onToggle(seat.id)
        }
    }

    const focusPoint = focus ? position(focus.angle, focus.radius) : null

    return (
        <div className={cn('relative mx-auto aspect-square w-full max-w-[40rem]', className)}>
            <p id={hintId} className='sr-only'>
                Use Tab or the arrow keys to move between seats, and Enter or Space to choose one.
            </p>
            <svg viewBox='-1.14 -1.14 2.28 2.28' className='block h-full w-full' role='group' aria-label='Seating plan of the planetarium dome' aria-describedby={hintId}>
                <circle r={0.62} fill='none' stroke='var(--c-brass)' strokeOpacity={0.09} strokeWidth={0.26} />
                <path d={arcPath(1.04, Math.PI + 0.2, Math.PI - 0.2)} fill='none' stroke='var(--c-hair-2)' strokeWidth={0.006} />
                <path d={arcPath(1.075, Math.PI + 0.2, Math.PI - 0.2)} fill='none' stroke='var(--c-hair)' strokeWidth={0.004} strokeDasharray='0.01 0.02' />

                {Array.from({ length: 72 }, (_, index) => {
                    const angle = (index / 72) * Math.PI * 2

                    if (Math.abs(angle - Math.PI) < 0.22) return null

                    const inner = position(angle, 1.04)
                    const outer = position(angle, index % 6 === 0 ? 1.075 : 1.058)

                    return <line key={index} x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} stroke='var(--c-hair-2)' strokeWidth={0.004} />
                })}

                <text x={0} y={1.1} textAnchor='middle' fill='var(--c-star-3)' fontSize={0.045} fontFamily='var(--font-app-mono, monospace)' letterSpacing={0.01}>
                    ENTRANCE
                </text>

                <text x={0} y={-0.505} textAnchor='middle' fill='var(--c-brass)' fontSize={0.034} fontFamily='var(--font-app-mono, monospace)' letterSpacing={0.008} opacity={0.75}>
                    BEST VIEW
                </text>

                <circle r={0.13} fill='var(--c-ink-2)' stroke='var(--c-hair-2)' strokeWidth={0.005} />
                <circle r={0.05} fill='none' stroke='var(--c-brass)' strokeWidth={0.005} />
                <line x1={-0.09} y1={0} x2={-0.06} y2={0} stroke='var(--c-brass)' strokeWidth={0.004} />
                <line x1={0.06} y1={0} x2={0.09} y2={0} stroke='var(--c-brass)' strokeWidth={0.004} />
                <line x1={0} y1={-0.09} x2={0} y2={-0.06} stroke='var(--c-brass)' strokeWidth={0.004} />
                <line x1={0} y1={0.06} x2={0} y2={0.09} stroke='var(--c-brass)' strokeWidth={0.004} />
                <text x={0} y={0.2} textAnchor='middle' fill='var(--c-star-3)' fontSize={0.032} fontFamily='var(--font-app-mono, monospace)'>
                    STAR PROJECTOR
                </text>

                {ringLabels.map(ring => {
                    const point = position(Math.PI / 3, ringRadii[ring])

                    return (
                        <text key={ring} x={point.x} y={point.y + 0.014} textAnchor='middle' fill='var(--c-star-3)' fontSize={0.04} fontFamily='var(--font-app-mono, monospace)'>
                            {ring}
                        </text>
                    )
                })}

                {SEATS.map(seat => {
                    const { x, y } = position(seat.angle, seat.radius)
                    const isTaken = taken.has(seat.id)
                    const isSelected = selectedSet.has(seat.id)
                    const isHeld = seat.kind === 'held'
                    const unavailable = isTaken || isHeld
                    const state = isSelected ? 'selected' : isTaken ? 'taken' : 'available'
                    const rotation = (seat.angle * 180) / Math.PI

                    return (
                        <g
                            key={seat.id}
                            data-seat={seat.id}
                            transform={`translate(${x} ${y}) rotate(${rotation})`}
                            role='checkbox'
                            aria-checked={isSelected}
                            aria-disabled={unavailable}
                            aria-label={describe(seat, state)}
                            tabIndex={unavailable ? -1 : 0}
                            data-cursor={unavailable ? undefined : 'link'}
                            onClick={() => {
                                if (!unavailable) onToggle(seat.id)
                            }}
                            onKeyDown={event => handleKey(event, seat, unavailable)}
                            onMouseEnter={() => setFocus(seat)}
                            onMouseLeave={() => setFocus(current => (current?.id === seat.id ? null : current))}
                            onFocus={() => setFocus(seat)}
                            onBlur={() => setFocus(current => (current?.id === seat.id ? null : current))}
                            className={cn('group outline-none transition-opacity', !unavailable && 'cursor-pointer')}
                        >
                            <rect x={-0.045} y={-0.045} width={0.09} height={0.09} fill='transparent' />
                            <circle r={0.044} fill='none' stroke='var(--c-brass)' strokeWidth={0.005} className='opacity-0 group-focus-visible:opacity-100' />
                            {seat.kind === 'wheelchair' ? (
                                <rect
                                    x={-0.028}
                                    y={-0.022}
                                    width={0.056}
                                    height={0.044}
                                    rx={0.008}
                                    fill={isSelected ? 'var(--c-brass)' : isTaken ? 'var(--c-ink-4)' : 'transparent'}
                                    stroke={isSelected ? 'var(--c-brass)' : isTaken ? 'var(--c-hair)' : 'var(--c-star-2)'}
                                    strokeWidth={0.005}
                                    strokeDasharray={isTaken ? undefined : '0.012 0.006'}
                                />
                            ) : (
                                <>
                                    <rect
                                        x={-0.029}
                                        y={-0.014}
                                        width={0.058}
                                        height={0.034}
                                        rx={0.009}
                                        fill={isSelected ? 'var(--c-brass)' : isTaken ? 'var(--c-ink-4)' : 'transparent'}
                                        stroke={isSelected ? 'var(--c-brass)' : isTaken ? 'var(--c-hair)' : isHeld ? 'var(--c-hair-2)' : focus?.id === seat.id ? 'var(--c-brass)' : 'var(--c-star-3)'}
                                        strokeWidth={0.005}
                                        strokeDasharray={isHeld ? '0.008 0.006' : undefined}
                                    />
                                    <path
                                        d='M -0.026 -0.024 Q 0 -0.034 0.026 -0.024'
                                        fill='none'
                                        stroke={isSelected ? 'var(--c-brass)' : isTaken ? 'var(--c-hair)' : isHeld ? 'var(--c-hair-2)' : 'var(--c-star-3)'}
                                        strokeWidth={0.005}
                                        strokeLinecap='round'
                                    />
                                </>
                            )}
                        </g>
                    )
                })}
            </svg>

            {focus && focusPoint && (
                <div
                    className='pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-[calc(100%+14px)] whitespace-nowrap border border-hair bg-ink-2/95 px-3 py-2 text-center backdrop-blur'
                    style={{ left: `${((focusPoint.x + 1.14) / 2.28) * 100}%`, top: `${((focusPoint.y + 1.14) / 2.28) * 100}%` }}
                >
                    <p className='label-mono text-star'>
                        {`Ring ${focus.ring} · Seat ${focus.number}`}
                    </p>
                    <p className='label-mono mt-0.5 text-[0.6rem] text-brass'>
                        {focus.kind === 'held'
                            ? 'Presenter'
                            : focus.kind === 'wheelchair'
                                ? 'Wheelchair space'
                                : taken.has(focus.id)
                                    ? 'Taken'
                                    : focus.best
                                        ? 'Best view'
                                        : 'Available'}
                    </p>
                </div>
            )}
        </div>
    )
}

export const SeatLegend = (): ReactNode => (
    <ul className='label-mono flex flex-wrap gap-x-6 gap-y-3 text-[0.65rem] text-star-2'>
        <li className='flex items-center gap-2'>
            <span className='inline-block h-2.5 w-4 rounded-[3px] border border-star-3' />
            Available
        </li>
        <li className='flex items-center gap-2'>
            <span className='inline-block h-2.5 w-4 rounded-[3px] border border-brass bg-brass' />
            Your seats
        </li>
        <li className='flex items-center gap-2'>
            <span className='inline-block h-2.5 w-4 rounded-[3px] border border-hair bg-ink-4' />
            Taken
        </li>
        <li className='flex items-center gap-2'>
            <span className='inline-block h-2.5 w-4 rounded-[3px] border border-dashed border-star-2' />
            Wheelchair space
        </li>
        <li className='flex items-center gap-2'>
            <span className='inline-block h-2.5 w-4 rounded-[3px] bg-brass/20' />
            Best view
        </li>
    </ul>
)
